import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantManagementService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './ShipDetailPage.css';

const fmt = (d) => d ? new Date(d).toLocaleDateString('ar-SA') : '—';
const fmtNum = (n) => n ? Number(n).toLocaleString('ar-SA') : '0';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1').replace('/api/v1', '');

// field_value is stored as JSON string: "value" → strip quotes
const parseVal = (v) => {
  if (!v) return null;
  try { const p = JSON.parse(v); return typeof p === 'string' ? p : v; } catch { return v; }
};

// Build absolute URL from relative /uploads/... paths
const absUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return null;
};

const isImage = (url) => /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
const isPDF   = (url) => /\.pdf$/i.test(url);

const VSTATUS = {
  verified: { cls: 'sdp__vs--ok',   icon: '✅', text: 'موثّق'        },
  rejected: { cls: 'sdp__vs--bad',  icon: '❌', text: 'مرفوض'       },
  pending:  { cls: 'sdp__vs--wait', icon: '⏳', text: 'قيد المراجعة' },
};

const APPROVAL = {
  pending:  { text: 'قيد المراجعة', cls: 'sdp__badge--yellow' },
  approved: { text: 'معتمد',        cls: 'sdp__badge--green'  },
  rejected: { text: 'مرفوض',       cls: 'sdp__badge--red'    },
};

const ShipDetailPage = () => {
  const { tenantId, shipId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [ship, setShip] = useState(null);
  const [loading, setLoading] = useState(true);

  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actioning, setActioning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await tenantManagementService.getShipDetails(tenantId, shipId);
      setShip(data);
    } catch {
      toast.error('فشل تحميل بيانات الشيب');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId, shipId]);

  const handleApprove = async () => {
    setActioning(true);
    try {
      await tenantManagementService.approveShip(tenantId, shipId, approveNotes);
      toast.success('تم قبول الشيب بنجاح');
      setApproveModal(false);
      load();
    } catch { toast.error('فشلت عملية القبول'); }
    finally { setActioning(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('يجب إدخال سبب الرفض'); return; }
    setActioning(true);
    try {
      await tenantManagementService.rejectShip(tenantId, shipId, rejectReason);
      toast.success('تم رفض الشيب');
      setRejectModal(false);
      load();
    } catch { toast.error('فشلت عملية الرفض'); }
    finally { setActioning(false); }
  };

  if (loading) return (
    <div className="sdp sdp--loading">
      <div className="sdp__spinner" />
      <span>جاري التحميل...</span>
    </div>
  );

  if (!ship) return (
    <div className="sdp sdp--error">
      <p>الشيب غير موجود</p>
      <button onClick={() => navigate(-1)}>العودة</button>
    </div>
  );

  const ap = APPROVAL[ship.approval_status] || APPROVAL.pending;

  return (
    <div className="sdp">
      {/* Header */}
      <div className="sdp__header">
        <button className="sdp__back" onClick={() => navigate('/admin/ships-review')}>
          ← العودة لقائمة الأشياب
        </button>
        <div className="sdp__title-row">
          <div className="sdp__avatar">{(ship.name || 'ش')[0].toUpperCase()}</div>
          <div>
            <h1 className="sdp__title">{ship.name}</h1>
            <div className="sdp__meta">
              <span className="sdp__code">{ship.ship_code || ship.code}</span>
              <span className="sdp__tenant" onClick={() => navigate(`/admin/tenants/${tenantId}`)}>
                🏢 {ship.tenant_name}
              </span>
            </div>
          </div>
          <span className={`sdp__badge ${ap.cls}`}>{ap.text}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="sdp__actions">
        {ship.approval_status !== 'approved' && (
          <button className="sdp__btn sdp__btn--approve" onClick={() => setApproveModal(true)}>
            ✓ قبول الشيب
          </button>
        )}
        {ship.approval_status !== 'rejected' && (
          <button className="sdp__btn sdp__btn--reject" onClick={() => setRejectModal(true)}>
            ✕ رفض الشيب
          </button>
        )}
        {ship.rejection_reason && (
          <div className="sdp__rejection-note">
            ⚠️ سبب الرفض: {ship.rejection_reason}
          </div>
        )}
        {ship.approval_notes && (
          <div className="sdp__approval-note">
            ✅ ملاحظات القبول: {ship.approval_notes}
          </div>
        )}
      </div>

      <div className="sdp__body">
        {/* Info Section */}
        <div className="sdp__card">
          <div className="sdp__card-title-row">
            <h2 className="sdp__card-title">معلومات الشيب</h2>
            {(ship.google_maps_url || (ship.latitude && ship.longitude)) && (
              <a
                className="sdp__gmap-btn"
                href={
                  ship.google_maps_url
                  || `https://www.google.com/maps?q=${ship.latitude},${ship.longitude}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                📍 معاينة على خرائط جوجل
              </a>
            )}
          </div>
          <div className="sdp__grid">
            <div className="sdp__field"><span className="sdp__label">الاسم</span><span className="sdp__value">{ship.name}</span></div>
            <div className="sdp__field"><span className="sdp__label">الاسم (عربي)</span><span className="sdp__value">{ship.name_ar || '—'}</span></div>
            <div className="sdp__field"><span className="sdp__label">الكود</span><span className="sdp__value sdp__value--code">{ship.ship_code || ship.code || '—'}</span></div>
            <div className="sdp__field"><span className="sdp__label">المدينة</span><span className="sdp__value">{ship.city || '—'}</span></div>
            <div className="sdp__field"><span className="sdp__label">العنوان</span><span className="sdp__value">{ship.address || ship.formatted_address || '—'}</span></div>
            <div className="sdp__field"><span className="sdp__label">الحي</span><span className="sdp__value">{ship.district || '—'}</span></div>
            <div className="sdp__field"><span className="sdp__label">الطاقة اليومية</span><span className="sdp__value">{fmtNum(ship.capacity_daily_tons)} طن</span></div>
            <div className="sdp__field"><span className="sdp__label">جهة الاتصال</span><span className="sdp__value">{ship.contact_phone || '—'}</span></div>
            <div className="sdp__field"><span className="sdp__label">البريد</span><span className="sdp__value">{ship.contact_email || '—'}</span></div>
            <div className="sdp__field">
              <span className="sdp__label">نوع المياه</span>
              <span className="sdp__value">
                {ship.serves_drinking_water && '🚰 شرب '}
                {ship.serves_non_drinking_water && '🏗️ غير صالح'}
                {!ship.serves_drinking_water && !ship.serves_non_drinking_water && '—'}
              </span>
            </div>
            <div className="sdp__field"><span className="sdp__label">تاريخ التقديم</span><span className="sdp__value">{fmt(ship.submitted_at)}</span></div>
            <div className="sdp__field"><span className="sdp__label">تاريخ الموافقة</span><span className="sdp__value">{fmt(ship.approved_at)}</span></div>
          </div>
        </div>

        {/* Stats */}
        <div className="sdp__card">
          <h2 className="sdp__card-title">الإحصائيات</h2>
          <div className="sdp__stats-row">
            <div className="sdp__stat"><span className="sdp__stat-val">{fmtNum(ship.total_orders)}</span><span className="sdp__stat-lbl">إجمالي الطلبات</span></div>
            <div className="sdp__stat"><span className="sdp__stat-val">{fmtNum(ship.completed_orders)}</span><span className="sdp__stat-lbl">تم التوصيل</span></div>
            <div className="sdp__stat"><span className="sdp__stat-val">{Number(ship.order_revenue || 0).toLocaleString('ar-SA')} ﷼</span><span className="sdp__stat-lbl">الإيرادات</span></div>
            <div className="sdp__stat"><span className="sdp__stat-val">⭐ {Number(ship.avg_rating || 0).toFixed(1)}</span><span className="sdp__stat-lbl">التقييم</span></div>
          </div>
        </div>

        {/* Water Types */}
        {ship.water_types && ship.water_types.length > 0 && (
          <div className="sdp__card">
            <h2 className="sdp__card-title">أنواع المياه</h2>
            <div className="sdp__wt-list">
              {ship.water_types.map((wt, i) => (
                <div key={i} className="sdp__wt-item">
                  <span>{wt.name_ar || wt.name} {wt.is_drinkable ? '🚰' : '🏗️'}</span>
                  <span className="sdp__wt-price">
                    {wt.stock_tons ? `${fmtNum(wt.stock_tons)} طن` : '—'}
                    {wt.is_available ? ' ✅' : ' ❌'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attachments */}
        <div className="sdp__card">
          <h2 className="sdp__card-title">المرفقات والوثائق</h2>
          {(() => {
            const reqFiles = (ship.requirement_files || []).map(f => ({
              ...f,
              _url: absUrl(parseVal(f.field_value)),
            })).filter(f => f._url);

            const imgFiles = (ship.images || []).map(img => ({
              _url: absUrl(img.image_url),
              field_name_ar: img.caption_ar || img.image_type || 'صورة',
              verification_status: img.verification_status || 'pending',
              group_name_ar: 'صور المحطة',
            })).filter(f => f._url);

            const profileImg = ship.profile_image_url ? [{
              _url: absUrl(ship.profile_image_url),
              field_name_ar: 'الصورة الرئيسية',
              verification_status: 'verified',
              group_name_ar: 'صور المحطة',
            }] : [];

            const all = [...profileImg, ...reqFiles, ...imgFiles];

            if (all.length === 0) return (
              <div className="sdp__empty-imgs">
                <span>📂</span>
                <p>لا توجد مرفقات أو وثائق مرفوعة حتى الآن</p>
              </div>
            );

            // Group by group_name_ar
            const groups = {};
            all.forEach(f => {
              const g = f.group_name_ar || 'مرفقات';
              if (!groups[g]) groups[g] = [];
              groups[g].push(f);
            });

            return Object.entries(groups).map(([groupName, files]) => (
              <div key={groupName} className="sdp__attach-group">
                <div className="sdp__attach-group-title">{groupName}</div>
                <div className="sdp__attach-grid">
                  {files.map((f, i) => {
                    const url = f._url;
                    const vs = VSTATUS[f.verification_status] || VSTATUS.pending;
                    return (
                      <div key={i} className="sdp__attach-item">
                        <a href={url} target="_blank" rel="noopener noreferrer" className="sdp__attach-preview">
                          {isImage(url) ? (
                            <img src={url} alt={f.field_name_ar} />
                          ) : isPDF(url) ? (
                            <div className="sdp__pdf-thumb">📄<span>PDF</span></div>
                          ) : (
                            <div className="sdp__pdf-thumb">📎<span>ملف</span></div>
                          )}
                        </a>
                        <div className="sdp__attach-meta">
                          <span className="sdp__attach-name">{f.field_name_ar}</span>
                          <span className={`sdp__vs ${vs.cls}`}>{vs.icon} {vs.text}</span>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="sdp__attach-dl">↗ فتح</a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Approve Modal */}
      {approveModal && (
        <div className="sdp__overlay" onClick={() => setApproveModal(false)}>
          <div className="sdp__modal" onClick={e => e.stopPropagation()}>
            <h3>✅ قبول الشيب</h3>
            <p>هل أنت متأكد من قبول الشيب <strong>{ship.name}</strong>؟</p>
            <textarea
              className="sdp__textarea"
              placeholder="ملاحظات القبول (اختياري)..."
              value={approveNotes}
              onChange={e => setApproveNotes(e.target.value)}
              rows={3}
            />
            <div className="sdp__modal-note sdp__modal-note--info">
              سيصبح الشيب نشطاً ومعتمداً ويستقبل الطلبات فوراً.
            </div>
            <div className="sdp__modal-actions">
              <button className="sdp__btn sdp__btn--ghost" onClick={() => setApproveModal(false)}>إلغاء</button>
              <button className="sdp__btn sdp__btn--approve" onClick={handleApprove} disabled={actioning}>
                {actioning ? '...' : 'تأكيد القبول'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="sdp__overlay" onClick={() => setRejectModal(false)}>
          <div className="sdp__modal" onClick={e => e.stopPropagation()}>
            <h3>❌ رفض الشيب</h3>
            <p>يرجى إدخال سبب رفض الشيب <strong>{ship.name}</strong>:</p>
            <textarea
              className="sdp__textarea"
              placeholder="سبب الرفض (مطلوب)..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
            />
            <div className="sdp__modal-note sdp__modal-note--warn">
              سيُوقف الشيب ولن يستقبل أي طلبات جديدة.
            </div>
            <div className="sdp__modal-actions">
              <button className="sdp__btn sdp__btn--ghost" onClick={() => setRejectModal(false)}>إلغاء</button>
              <button className="sdp__btn sdp__btn--reject" onClick={handleReject} disabled={actioning}>
                {actioning ? '...' : 'تأكيد الرفض'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipDetailPage;
